import moment from 'moment';
import { defaultEnvironment } from '../environment';
import { Employee, Place, UserEmployeeTraceStats, daysMap, periodToParams } from '../models';
import { AppService } from './app';
import { parseCommutingTripOccurrence } from './commuting-trip';
import { HttpService } from './http';
import { backendBikeReasonsMap, backendDemotivationReasonsMap, backendMobilitySurveyTransportModes, bikeReasonsMap, demotivationReasonsMap, transportModesMap } from './mobility-survey';
import { parsePartner } from './partner';
import { UserService, parseUserReferenceTripOccurrence } from './user';
export class EmployeeService {
  static async createEmployee(partner, {
    userId,
    firstName,
    lastName,
    professionalEmail,
    phoneNumber,
    eulaAccepted,
    privacyPolicyAccepted,
    receiveNewsletter,
    surveyAnswers,
    invitationCode,
    role
  }) {
    try {
      const params = userId ? {
        user: {
          id: userId
        }
      } : {};
      if (firstName) params.first_name = firstName;
      if (lastName) params.last_name = lastName;
      if (professionalEmail) params.professional_email = professionalEmail;
      if (phoneNumber) params.phone_number = phoneNumber;
      if (eulaAccepted !== undefined) params.cgu_accepted = eulaAccepted;
      if (privacyPolicyAccepted !== undefined) params.privacy_policy_accepted = privacyPolicyAccepted;
      if (receiveNewsletter) params.receive_newsletter = receiveNewsletter;
      if (surveyAnswers) params.survey = surveyAnswers;
      if (invitationCode) params.invitation_code = invitationCode;
      if (role) params.role = backendRolesMap[role];
      const data = await HttpService.post('v2', `/partners/${partner.id}/employees`, [], [], JSON.stringify(params), {
        expectedStatus: 201,
        partner: partner.code || ''
      });
      return parseEmployee(data);
    } catch (err) {
      console.error('[EmployeeService][createEmployee]', err);
      throw err;
    }
  }
  static async getEmployees({
    query
  } = {}) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      const queryParams = [{
        key: 'join_statuses',
        value: 'EMPLOYEE_JOIN,EMPLOYEE_WAITING_FOR_APPROVAL'
      }];
      if (query) queryParams.push({
        key: 'query',
        value: query
      });
      const data = (await HttpService.get('v2', `/users/${UserService.currentUser.id}/employees`, queryParams)).sort((a, b) => {
        if (a.join_status === 'EMPLOYEE_JOIN') return b.join_status === 'EMPLOYEE_JOIN' ? 0 : -1;
        if (b.join_status === 'EMPLOYEE_JOIN') return 1;
        return 0;
      });
      return data.map(parseEmployeeAndPartner).reduce((res, {
        employee,
        partner
      }) => {
        const contracts = partner?.contracts || [];
        if (employee.role !== 'administrator') contracts.filter(({
          contractTemplate: {
            code
          }
        }) => code !== 'geovelo-entreprise-holding');
        if (partner && contracts.length > 0) res.push({
          employee,
          partner
        });
        return res;
      }, []);
    } catch (err) {
      console.error('[EmployeeService][getEmployees]', err);
      throw err;
    }
  }
  static async getEmployeesInvitations({
    query
  } = {}) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      const queryParams = [];
      if (query) queryParams.push({
        key: 'query',
        value: query
      });
      const data = await HttpService.get('v1', `/user_employee_invitations`, queryParams);
      return data.map(parseEmployeeAndPartner).reduce((res, {
        employee,
        partner
      }) => {
        const contracts = (partner?.contracts || []).filter(({
          contractTemplate: {
            code
          }
        }) => code !== 'geovelo-entreprise-holding');
        if (partner && contracts.length > 0) res.push({
          employee,
          partner
        });
        return res;
      }, []);
    } catch (err) {
      console.error('[EmployeeService][getEmployeesInvitations]', err);
      throw err;
    }
  }
  static async resendInvitation({
    employee
  }) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      await HttpService.get('v1', `/user_employee_invitations/${employee.id}/resend`);
      return true;
    } catch (err) {
      console.error('[EmployeeService][resendInvitation]', err);
      throw err;
    }
  }
  static async getPartnerEmployee({
    partner,
    id
  }) {
    try {
      const data = await HttpService.get('v2', `/partners/${partner.id}/employees/${id}`, [], [], null, {
        partner: partner.code
      });
      if (!data) throw new Error('employee not found');
      return parseEmployee(data);
    } catch (err) {
      console.error('[EmployeeService][getPartnerEmployee]', err);
      throw err;
    }
  }
  static async linkToUser(partnerCode, token) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      const data = await HttpService.get('v1', `/employees/${token}/link_user`, [], [], null, {
        partner: partnerCode
      });
      if (!data) throw new Error('employee not found');
      return parseEmployeeAndPartner(data);
    } catch (err) {
      console.error('[EmployeeService][linkToUser]', err);
      throw err;
    }
  }
  static async updateEmployee({
    id: partnerId,
    code: partner
  }, employeeId, {
    role,
    isFMDBeneficiary,
    firstName,
    lastName,
    professionalEmail,
    companyEmployeeId,
    eulaAccepted,
    privacyPolicyAccepted,
    receiveNewsletter,
    surveyAnswers
  }) {
    try {
      const params = {};
      if (role !== undefined) params.role = backendRolesMap[role];
      if (isFMDBeneficiary !== undefined) params.can_declare_fmd = isFMDBeneficiary;
      if (firstName !== undefined) params.first_name = firstName;
      if (lastName !== undefined) params.last_name = lastName;
      if (professionalEmail !== undefined) params.professional_email = professionalEmail;
      if (companyEmployeeId !== undefined) params.company_employee_id = companyEmployeeId;
      if (eulaAccepted !== undefined) params.cgu_accepted = eulaAccepted;
      if (privacyPolicyAccepted !== undefined) params.privacy_policy_accepted = privacyPolicyAccepted;
      if (receiveNewsletter !== undefined) params.receive_newsletter = receiveNewsletter;
      if (surveyAnswers) params.survey = surveyAnswers;
      const data = await HttpService.patch('v2', `/partners/${partnerId}/employees/${employeeId}`, [], [], JSON.stringify(params), {
        partner
      });
      return parseEmployee(data);
    } catch (err) {
      console.error('[EmployeeService][updateEmployee]', err);
      throw err;
    }
  }
  static async deleteEmployee({
    id: partnerId,
    code: partner
  }, employeeId) {
    try {
      await HttpService.delete('v2', `/partners/${partnerId}/employees/${employeeId}`, [], [], null, partner ? {
        partner
      } : undefined);
      return true;
    } catch (err) {
      console.error('[EmployeeService][deleteEmployee]', err);
      throw err;
    }
  }
  static async getUserTraceStats(employeeId, period) {
    try {
      const data = await HttpService.get('v1', `/employees/${employeeId}/user_trace_stats`, [...periodToParams(period), {
        key: 'query',
        value: '{count,distance,co2e,home_work_reference_trips_count,home_work_reference_trips_distance,home_work_co2_g_saved,data{unit,count,distance,co2e,home_work_reference_trips_count,home_work_reference_trips_distance,home_work_co2_g_saved}}'
      }]);
      return parseUserEmployeeTraceStats(data);
    } catch (err) {
      console.error('[EmployeeService][getUserTraceStats]', err);
      throw err;
    }
  }
  static async getFMDStats({
    partner,
    employeeId,
    startDate,
    endDate
  }) {
    try {
      const {
        amount_to_pay: allowance,
        count_days_total: daysCount,
        sum_distance_total: totalDistance,
        count_home_to_work_journeys: homeToWorkCount,
        count_work_to_home_journeys: workToHomeCount
      } = await HttpService.get('v1', `/employees/${employeeId}/fmd_attestations_stats`, [{
        key: 'period',
        value: 'custom'
      }, {
        key: 'date_start',
        value: startDate.format('YYYY-MM-DD')
      }, {
        key: 'date_end',
        value: endDate.format('YYYY-MM-DD')
      }], [], null, {
        partner: partner.code
      });
      return {
        allowance: allowance || 0,
        daysCount: daysCount || 0,
        totalDistance: totalDistance || 0,
        homeToWorkCount: homeToWorkCount || 0,
        workToHomeCount: workToHomeCount || 0
      };
    } catch (err) {
      console.error('[EmployeeService][getFMDStats]', err);
      throw err;
    }
  }
  static async getFMDEstimation({
    partner,
    employeeId,
    startDate,
    endDate
  }) {
    try {
      const {
        amount_to_pay: allowance,
        count_days_total: daysCount,
        sum_distance_manually_declared_journeys,
        sum_distance_user_trace_journeys,
        count_home_to_work_journeys: homeToWorkCount,
        count_work_to_home_journeys: workToHomeCount,
        min_days_count_per_month
      } = await HttpService.get('v2', `/employees/${employeeId}/fmd_attestations_estimate`, [{
        key: 'date_start',
        value: startDate.format('YYYY-MM-DD')
      }, {
        key: 'date_end',
        value: endDate.format('YYYY-MM-DD')
      }], [], null, {
        partner: partner.code
      });
      return {
        allowance: allowance || 0,
        daysCount: daysCount || 0,
        distance: (sum_distance_manually_declared_journeys || 0) + (sum_distance_user_trace_journeys || 0),
        outwardCount: homeToWorkCount || 0,
        returnCount: workToHomeCount || 0,
        dayThresholdNotReached: (daysCount || 0) < (min_days_count_per_month || 0)
      };
    } catch (err) {
      console.error('[EmployeeService][getFMDEstimation]', err);
      throw err;
    }
  }
  static async getCertificates({
    partner,
    employeeId,
    page,
    pageSize,
    startDate,
    endDate,
    query,
    excludeInvalid
  }) {
    try {
      const params = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, {
        key: 'period',
        value: 'custom'
      }, {
        key: 'exclude_null_amount_to_pay',
        value: false
      }, {
        key: 'exclude_invalid_attestation',
        value: excludeInvalid || false
      }];
      if (startDate) params.push({
        key: 'date_start',
        value: startDate.format('YYYY-MM-DD')
      });
      if (endDate) params.push({
        key: 'date_end',
        value: endDate.format('YYYY-MM-DD')
      });
      if (query) params.push({
        key: 'query',
        value: query
      });
      const {
        count,
        results
      } = await HttpService.get('v2', `/employees/${employeeId}/fmd_attestations`, params, [], null, {
        partner: partner.code
      });
      return {
        count,
        certificates: results.map(data => parseCertificate(data))
      };
    } catch (err) {
      console.error('[EmployeeService][getCertificates]', err);
      throw err;
    }
  }
  static async createCertificate({
    partner,
    employeeId,
    startDate,
    endDate
  }) {
    try {
      const data = await HttpService.post('v2', `/employees/${employeeId}/fmd_attestations`, [], [], JSON.stringify({
        employee: employeeId,
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD')
      }), {
        partner: partner.code
      });
      return parseCertificate(data);
    } catch (err) {
      console.error('[EmployeeService][generateCertificate]', err);
      throw err;
    }
  }
  static async updateCertificate({
    partner,
    employeeId,
    certificateId,
    isValid
  }) {
    try {
      const props = {};
      if (isValid !== undefined) props.is_valid = isValid;
      const data = await HttpService.patch('v2', `/employees/${employeeId}/fmd_attestations/${certificateId}`, [], [], JSON.stringify(props), {
        partner: partner.code
      });
      return parseCertificate(data);
    } catch (err) {
      console.error('[EmployeeService][updateCertificate]', err);
      throw err;
    }
  }
  static async getTransportHabits({
    employeeId
  }) {
    try {
      const data = await HttpService.get('v1', `/employees/${employeeId}/transport_habits`, [], [], null, {
        expectedStatus: 200
      });
      return parseTransportHabits(data);
    } catch (err) {
      if (typeof err === 'object' && err && 'status' in err && err.status === 404) return null;
      console.error('[EmployeeService][getTransportHabits]', err);
      throw err;
    }
  }
  static async addTransportHabits({
    employeeId,
    transportModes,
    alternativesTransportModes,
    bikeReasons,
    demotivationReasons,
    maxDurationInMinutes,
    readyToTryCycling,
    cyclingDisadvantageOther
  }) {
    try {
      const data = await HttpService.post('v1', `/employees/${employeeId}/transport_habits`, [], [], JSON.stringify({
        goes_to_work_by_bicycle: transportModes.includes('bicycle'),
        transportation_modes: transportModes.map(key => transportModesMap[key]),
        alternative_to_bike_transportation_modes: alternativesTransportModes.map(key => transportModesMap[key]),
        cycling_advantages: bikeReasons.map(key => bikeReasonsMap[key]),
        cycling_disadvantages: demotivationReasons.map(key => demotivationReasonsMap[key]),
        commute_duration_in_minutes: maxDurationInMinutes ? maxDurationInMinutes : null,
        ready_to_try_cycling: readyToTryCycling,
        cycling_disadvantage_other: cyclingDisadvantageOther ? cyclingDisadvantageOther : null
      }));
      return parseTransportHabits(data);
    } catch (err) {
      if (typeof err === 'object' && err && 'status' in err && err.status === 404) return null;
      console.error('[EmployeeService][addTransportHabits]', err);
      throw err;
    }
  }
  static async updateTransportHabits({
    employeeId,
    transportModes,
    alternativesTransportModes,
    bikeReasons,
    demotivationReasons,
    maxDurationInMinutes,
    readyToTryCycling,
    cyclingDisadvantageOther
  }) {
    try {
      const data = await HttpService.patch('v1', `/employees/${employeeId}/transport_habits`, [], [], JSON.stringify({
        goes_to_work_by_bicycle: transportModes.includes('bicycle'),
        transportation_modes: transportModes.map(key => transportModesMap[key]),
        alternative_to_bike_transportation_modes: alternativesTransportModes.map(key => transportModesMap[key]),
        cycling_advantages: bikeReasons.map(key => bikeReasonsMap[key]),
        cycling_disadvantages: demotivationReasons.map(key => demotivationReasonsMap[key]),
        commute_duration_in_minutes: maxDurationInMinutes ? maxDurationInMinutes : null,
        ready_to_try_cycling: readyToTryCycling,
        cycling_disadvantage_other: cyclingDisadvantageOther ? cyclingDisadvantageOther : null
      }));
      return parseTransportHabits(data);
    } catch (err) {
      if (typeof err === 'object' && err && 'status' in err && err.status === 404) return null;
      console.error('[EmployeeService][updateTransportHabits]', err);
      throw err;
    }
  }
  static async getEmployeeCO2Saving({
    userUid,
    partnerId
  }) {
    try {
      const {
        username,
        firstname,
        lastname,
        profile_picture,
        saved_co2_kg: savedCO2InKg,
        partner_name,
        partner_logo
      } = await HttpService.get('v1', `/user_co2_saved`, [{
        key: 'uid',
        value: userUid
      }, {
        key: 'partner_id',
        value: partnerId
      }]);
      return {
        employee: {
          name: [firstname, lastname].join(' ') || username,
          profilePicture: profile_picture ? `${defaultEnvironment.backendUrl}${profile_picture}` : null
        },
        partner: {
          title: partner_name,
          photo: partner_logo ? `${defaultEnvironment.backendUrl}${partner_logo}` : null
        },
        savedCO2InKg
      };
    } catch (err) {
      console.error('[EmployeeService][getEmployeeCO2Saving]', err);
      throw err;
    }
  }
}
const rolesMap = {
  ADMINISTRATOR_GLOBAL: 'administrator',
  ANIMATOR_GLOBAL: 'animator',
  EMPLOYEE: 'employee'
};
export const backendRolesMap = {
  administrator: 'ADMINISTRATOR_GLOBAL',
  animator: 'ANIMATOR_GLOBAL',
  employee: 'EMPLOYEE'
};
const employeeJoinStatus = {
  EMPLOYEE_JOIN: 'joined',
  EMPLOYEE_QUIT: 'left',
  EMPLOYEE_WAITING_FOR_APPROVAL: 'waitingForApproval',
  WAITING_USER_TO_JOIN: 'waitingUserToJoin'
};
export function parseEmployee({
  id,
  first_name,
  last_name,
  professional_email,
  company_employee_id,
  cgu_accepted,
  receive_newsletter,
  privacy_policy_accepted,
  role,
  can_declare_fmd,
  user,
  survey,
  partner,
  join_status,
  inscription_link,
  has_first_name,
  has_last_name,
  has_professional_email,
  has_transport_habits,
  department_is_missing,
  has_at_least_one_commuting_trip
}) {
  return new Employee(id || -1, first_name, last_name, professional_email, company_employee_id, Boolean(cgu_accepted), Boolean(receive_newsletter), Boolean(privacy_policy_accepted), role && rolesMap[role] || 'employee', Boolean(can_declare_fmd), user ? {
    id: user.id,
    email: user.email,
    username: user.username,
    profilePicture: user.profile_picture ? `${defaultEnvironment.backendUrl}${user.profile_picture}` : undefined,
    subgroupsIds: user.subgroups?.map(({
      group_id
    }) => group_id),
    joinedSites: user.joined_sites,
    joinedTeams: user.joined_departments,
    joinedSubsidiaries: user.joined_subsidiaries,
    joinedRegions: user.joined_regions,
    uid: user.unique_id
  } : undefined, partner ? {
    id: partner.id,
    icon: partner.icon ? `${defaultEnvironment.backendUrl}${partner.icon}` : null,
    title: partner.title || ''
  } : null, survey || {}, join_status && employeeJoinStatus[join_status], inscription_link || '', has_first_name, has_last_name, has_professional_email, has_transport_habits, department_is_missing, has_at_least_one_commuting_trip);
}
function parseEmployeeAndPartner(data) {
  const {
    partner
  } = data;
  return {
    employee: parseEmployee(data),
    partner: partner ? parsePartner({
      partner,
      is_default: true
    }) : null
  };
}
export function parseCertificate({
  id,
  type,
  created,
  updated,
  start_date,
  end_date,
  amount_to_pay: compensation,
  count_days_total: daysCount,
  count_days_user_trace_journeys: validatedDaysCount,
  sum_distance_manually_declared_journeys,
  sum_distance_user_trace_journeys,
  count_manually_declared_journeys: manuallyDeclaredJourneysCount,
  authorized_manually_declared_journeys_ratio: maxManuallyDeclaredJourneysRatio,
  count_user_trace_journeys: journeysCount,
  attestation_validation_limit_day: validationLimitDay,
  attestation_file: url,
  employee,
  occurrences,
  reference_trip_occurrences,
  commuting_trip_occurrences,
  geo_point_start,
  geo_point_start_title,
  geo_point_end,
  geo_point_end_title,
  is_temporary,
  is_valid,
  is_friday_compensable,
  is_monday_compensable,
  is_saturday_compensable,
  is_sunday_compensable,
  is_thursday_compensable,
  is_tuesday_compensable,
  is_wednesday_compensable
}) {
  const compensableDays = {
    monday: is_monday_compensable || false,
    tuesday: is_tuesday_compensable || false,
    wednesday: is_wednesday_compensable || false,
    thursday: is_thursday_compensable || false,
    friday: is_friday_compensable || false,
    saturday: is_saturday_compensable || false,
    sunday: is_sunday_compensable || false
  };
  return {
    id: id || -1,
    type: type === 'DAILY' ? 'daily' : type === 'DISTANCE' ? 'distance' : type === 'DAILY_AND_DISTANCE' ? 'dailyAndDistance' : type === 'FIXED' ? 'fixed' : undefined,
    created: created ? moment(created) : undefined,
    updated: updated ? moment(updated) : undefined,
    monthFirstDay: start_date ? moment(start_date).startOf('month') : undefined,
    monthLastDay: end_date ? moment(end_date).endOf('month') : undefined,
    compensation,
    validatedDaysCount,
    daysCount,
    validatedDistance: sum_distance_user_trace_journeys,
    totalDistance: typeof sum_distance_manually_declared_journeys === 'number' || typeof sum_distance_user_trace_journeys === 'number' ? (sum_distance_manually_declared_journeys || 0) + (sum_distance_user_trace_journeys || 0) : sum_distance_manually_declared_journeys,
    journeysCount,
    manuallyDeclaredJourneysCount,
    maxManuallyDeclaredJourneysRatio,
    validationLimitDay,
    url: url && url.indexOf('https') === -1 && url.indexOf('http') === -1 ? `${AppService.environment.backendUrl}${url}` : url,
    employee: employee ? parseEmployee(employee) : undefined,
    occurrences: occurrences?.map(parseUserReferenceTripOccurrence) || reference_trip_occurrences?.map(parseUserReferenceTripOccurrence).filter(({
      date
    }) => {
      const day = daysMap[date.get('day')];
      return compensableDays[day];
    }),
    commutingTripOccurrences: commuting_trip_occurrences?.map(parseCommutingTripOccurrence),
    home: geo_point_start ? new Place(-1, geo_point_start, geo_point_start_title ? {
      primaryText: geo_point_start_title
    } : undefined) : undefined,
    work: geo_point_end ? new Place(-1, geo_point_end, geo_point_end_title ? {
      primaryText: geo_point_end_title
    } : undefined) : undefined,
    isValid: is_valid || false,
    isTemporary: is_temporary || false
  };
}
function parseTransportHabits({
  id,
  goes_to_work_by_bicycle: useBicycleForHomeWorkJourneys,
  transportation_modes,
  alternative_to_bike_transportation_modes,
  cycling_advantages,
  cycling_disadvantages,
  commute_duration_in_minutes,
  ready_to_try_cycling: readyToTryCycling,
  cycling_disadvantage_other: cyclingDisadvantageOther
}) {
  return {
    id,
    useBicycleForHomeWorkJourneys,
    transportModes: transportation_modes.map(key => backendMobilitySurveyTransportModes[key]).filter(Boolean),
    alternativeTransportModes: alternative_to_bike_transportation_modes?.map(key => backendMobilitySurveyTransportModes[key]).filter(Boolean) || [],
    bikeReasons: cycling_advantages?.map(key => backendBikeReasonsMap[key]).filter(Boolean) || [],
    demotivationReasons: cycling_disadvantages?.map(key => backendDemotivationReasonsMap[key]).filter(Boolean) || [],
    maxDurationInMinutes: commute_duration_in_minutes === 60 || commute_duration_in_minutes === 45 || commute_duration_in_minutes === 30 || commute_duration_in_minutes === 15 ? commute_duration_in_minutes : null,
    readyToTryCycling,
    cyclingDisadvantageOther: cyclingDisadvantageOther ?? null
  };
}
function parseUserEmployeeTraceStats({
  count,
  distance,
  co2e,
  home_work_reference_trips_count,
  home_work_reference_trips_distance,
  home_work_co2_g_saved,
  data
}) {
  return new UserEmployeeTraceStats(count || 0, distance || 0, co2e !== null && co2e !== undefined ? co2e / 1000 : undefined, home_work_reference_trips_count ?? undefined, home_work_reference_trips_distance ?? undefined, home_work_co2_g_saved !== null && home_work_co2_g_saved !== undefined ? home_work_co2_g_saved / 1000 : undefined, (data || []).map(({
    unit: _unit,
    count,
    distance,
    co2e,
    home_work_reference_trips_count,
    home_work_reference_trips_distance,
    home_work_co2_g_saved
  }) => ({
    count: count || 0,
    distance: distance || 0,
    co2e: co2e !== null && co2e !== undefined ? co2e / 1000 : undefined,
    homeWorkCount: home_work_reference_trips_count ?? undefined,
    homeWorkDistance: home_work_reference_trips_distance ?? undefined,
    homeWorkSavedCO2InKg: home_work_co2_g_saved !== null && home_work_co2_g_saved !== undefined ? home_work_co2_g_saved / 1000 : undefined,
    unit: moment(_unit),
    _unit
  })));
}